/**
 * Grade de indicadores (item STATS): o formato dos cards e as opções de exibição.
 *
 * `value` do item é a lista de cards `{ value, label }`. Colunas, estilo e cor ficam nas
 * configs do item (`statsColumns`, `statsStyle`, `calloutColor`). O PDF (PdfEngine e
 * `pdf-renderer/`) desenha com estes mesmos valores; o editor, a lixeira, os favoritos e
 * a prévia antiga também.
 */
export interface DocumentStatEntry {
    value: string;
    label: string;
}

export type StatsStyleKey = 'soft' | 'tonal' | 'outline' | 'line';

export interface StatsStyleOption {
    key: StatsStyleKey;
    labelKey: string;
}

export const STATS_STYLES: readonly StatsStyleOption[] = [
    { key: 'soft', labelKey: 'document.edit.statsCard.styleSoft' },
    { key: 'tonal', labelKey: 'document.edit.statsCard.styleTonal' },
    { key: 'outline', labelKey: 'document.edit.statsCard.styleOutline' },
    { key: 'line', labelKey: 'document.edit.statsCard.styleLine' },
];

export const STATS_COLUMNS: readonly number[] = [2, 3, 4];

/** Mesmos tetos da API (stats-entries.util): o card precisa caber na folha. */
export const STATS_LIMITS = {
    MAX_ENTRIES: 12,
    MAX_VALUE_LENGTH: 24,
    MAX_LABEL_LENGTH: 120,
    INITIAL_ENTRIES: 3,
} as const;

export const DEFAULT_STATS_COLUMNS = 3;
export const DEFAULT_STATS_STYLE: StatsStyleKey = 'soft';

/** Cards vazios de um campo recém-criado. */
export function initialStatsEntries(): DocumentStatEntry[] {
    return Array.from({ length: STATS_LIMITS.INITIAL_ENTRIES }, () => ({ value: '', label: '' }));
}

/** Lê o `value` gravado como lista de cards; o que não for card é ignorado. */
export function parseStatsEntries(raw: unknown): DocumentStatEntry[] {
    if (!Array.isArray(raw)) return [];

    return raw
        .filter(el => el !== null && typeof el === 'object' && !Array.isArray(el))
        .map(el => {
            const record = el as Record<string, unknown>;
            return {
                value: typeof record['value'] === 'string' ? record['value'] : '',
                label: typeof record['label'] === 'string' ? record['label'] : '',
            };
        });
}

/** O card imprime? (mesma regra do PDF: número OU legenda). */
export function statEntryHasContent(entry: DocumentStatEntry): boolean {
    return !!entry.value.trim() || !!entry.label.trim();
}

/** Colunas pedidas, trazidas para 2 a 4; ausente => 3. */
export function resolveStatsColumns(raw: number | null | undefined): number {
    const value =
        typeof raw === 'number' && Number.isFinite(raw) ? Math.round(raw) : DEFAULT_STATS_COLUMNS;
    return Math.min(Math.max(value, 2), 4);
}

export function resolveStatsStyle(raw: string | null | undefined): StatsStyleKey {
    const key = raw?.trim().toLowerCase();
    return STATS_STYLES.find(s => s.key === key)?.key ?? DEFAULT_STATS_STYLE;
}

/** Duas listas iguais card a card (evita salvar o que não mudou). */
export function sameStatsEntries(a: DocumentStatEntry[], b: DocumentStatEntry[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((entry, i) => entry.value === b[i].value && entry.label === b[i].label);
}
